import React, { useCallback, useContext, useState } from 'react';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { OffsetModifier } from '../../../popper/types';
import IconButton, { Props as IconButtonProps } from '../../legacy/IconButton/IconButton';
import { DropdownStateContext } from '../../DropdownStateManager';
import { PopperDropdownManager, PopperDropdownContent } from '../../PopperDropdownLayout';
import { Props as PopperDropdownContentProps } from '../../PopperDropdownLayout/PopperDropdownContent';
import { Props as PopperDropdownManagerProps } from '../../PopperDropdownLayout/PopperDropdownManager';
import { setRefs } from '../../../../utils/refHelpers';

export type Props = {
  children: React.ReactNode;
  popoverChildren: React.ReactNode;
  className?: string;
  /**
   * The WAI-ARIA role the popover content fulfills. Recommended: `popoverRole="dialog"`
   */
  popoverRole?: string;
  /**
   * To be provided if `popoverRole="dialog"`
   */
  popoverAriaLabel?: string;
  popoverOffset?: OffsetModifier;
  onOpenBiData?: BiEvent;
  onCloseBiData?: BiEvent;
} & Pick<PopperDropdownContentProps, 'placement'> &
  Pick<PopperDropdownManagerProps, 'isInPopup'> &
  Omit<IconButtonProps, 'className'>;

/*
  Separate content component so we can make use of the DropdownStateContext provided
  by PopperDropdownManager without needing to use a child render function with DropdownStateContext.Consumer.

  Saves some renders.
*/
const Content: React.FC<Omit<Props, 'isInPopup'>> = ({
  innerRef,
  className,
  biData,
  onOpenBiData = biData,
  onCloseBiData = biData,
  children,
  placement,
  popoverRole,
  popoverOffset,
  popoverAriaLabel,
  popoverChildren,
  ...rest
}) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const { setToggleRef, isOpen } = useContext(DropdownStateContext);

  const refCallback = useCallback(
    node => setRefs([setToggleRef, setReferenceElement, innerRef], node),
    [setToggleRef, innerRef]
  );

  return (
    <>
      <IconButton
        aria-haspopup={true}
        aria-expanded={isOpen}
        className={className}
        innerRef={refCallback}
        biData={isOpen ? onCloseBiData : onOpenBiData}
        {...rest}
      >
        {children}
      </IconButton>
      <PopperDropdownContent
        placement={placement}
        usePortal
        role={popoverRole}
        aria-label={popoverAriaLabel}
        referenceElement={referenceElement}
        offsetModifier={popoverOffset}
      >
        {popoverChildren}
      </PopperDropdownContent>
    </>
  );
};

export const IconButtonWithPopover: React.FC<React.PropsWithChildren<Props>> = ({
  isInPopup,
  ...rest
}) => {
  return (
    <PopperDropdownManager isInPopup={isInPopup}>
      <Content {...rest} />
    </PopperDropdownManager>
  );
};
IconButtonWithPopover.displayName = 'IconButtonWithPopover';

export default IconButtonWithPopover;
